<template>
  <MenuBar class="shadow-1">
    <template #start>
      <div class="flex flex-row">
        <Button
          icon="pi pi-bars"
          severity="secondary"
          aria-label="Bookmark"
          class="mr-2 bg-white-alpha-30"
          @click="menuToggle"
        />
        <img
          id="logo"
          alt="logo"
          src="@/assets/logo.png"
          height="56"
          @click="router.push('/home')"
          class="cursor-pointer align-self-end"
        />
      </div>
      <img
        id="logo-mid"
        alt="logo"
        src="@/assets/logo-blue.jpg"
        height="56"
        @click="router.push('/home')"
        class="cursor-pointer align-self-end"
      />
      <SelectCompany
        id="select-company"
        :class="'md:inline-flex dropdown lg:w-5 md:w-6'"
        v-if="showCompanySelect"
      >
      </SelectCompany>
    </template>
    <template #end>
      <div class="flex flex-row align-items-center">
        <BackgroundProcessTray class="mr-3" v-if="showProgressTray" />
        <img
          v-show="company?.logoUrl"
          alt="company-logo"
          :src="company?.logoUrl"
          height="40"
          @click="router.push('/home')"
          class="cursor-pointer"
          id="company-logo"
          v-tooltip.bottom="company?.code + ' - ' + company?.name"
        />
        <Divider layout="vertical" />
        <div
          class="user-options"
          @click="toggle"
          aria-haspopup="true"
          aria-controls="overlay_tmenu"
        >
          <Avatar
            class="align-self-center text-sm"
            :label="userLetter"
            size="normal"
            style="background-color: #2563eb; color: #ffffff"
          />
          <div class="user-options-details hidden flex flex-column ml-3">
            <span class="font-semibold text-primary-900">{{ user?.name }}</span>
            <span class="text-sm text-gray-500">{{ user?.email }}</span>
          </div>
          <i class="pi pi-angle-down align-self-center ml-2"></i>
        </div>
        <TieredMenu ref="menu" id="overlay_tmenu" :model="items" popup />
      </div>
    </template>
  </MenuBar>
  <UpdatePasswordDialog />
</template>
<script lang="ts" setup>
  import router from '@/router'
  import { useChatStore, useUserStore } from '@/store'
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import { ObservableService } from '../services'
  import { storeToRefs } from 'pinia'
  import UpdatePasswordDialog from '@/modules/auth/views/UpdatePasswordDialog.vue'
  import roleNames from '../utils/rolesName'
  import BackgroundProcessTray from './BackgroundProcessTray.vue'

  const userStore = useUserStore()
  const chatStore = useChatStore()
  const { user, company } = storeToRefs(userStore)

  const observableService = ObservableService.getInstance()
  const isMenuOpen = ref(true)
  const IsOpenPasswordDialog = ref(false)
  const menu = ref()
  import SelectCompany from './SelectCompany.vue'

  const logout = () => {
    userStore.logout()
    userStore.$reset()
    chatStore.clearContext()
    chatStore.clearMessages()
    router.push('/login')
  }

  const menuToggle = () => {
    isMenuOpen.value = !isMenuOpen.value
    observableService.emitValue('toggle-menu', isMenuOpen.value)
  }

  const changePasswordDialogToggle = () => {
    observableService.emitValue('update-password', !IsOpenPasswordDialog.value)
  }

  onMounted(() => {
    observableService.createObservable('toggle-menu')
    observableService.createObservable('update-password')

    observableService.getObservable('toggle-menu')?.subscribe((value) => {
      if (value !== undefined && value !== null) isMenuOpen.value = value
    })
    observableService
      .getObservable('update-password')
      ?.subscribe((value) => (IsOpenPasswordDialog.value = value))
  })

  onUnmounted(() => {
    observableService.destroyObservable('toggle-menu')
    observableService.destroyObservable('update-password')
  })

  const toggle = (event: any) => {
    menu.value.toggle(event)
  }

  const userLetter = computed(() => {
    if (user.value?.name.trim().includes('-')) {
      user.value.name = user.value.name.split('-')[1].trim()
    }

    const words = user.value?.name.trim().split(' ') ?? 'NONE'
    if (words?.length > 2) {
      return (
        words[0].charAt(0).toUpperCase() +
        words[2].charAt(0).toLocaleUpperCase()
      )
    } else if (words.length === 1) {
      return words[0].charAt(0).toLocaleUpperCase()
    } else {
      return (
        words[0].charAt(0).toLocaleUpperCase() +
        words[1].charAt(0).toLocaleUpperCase()
      )
    }
  })

  const showCompanySelect = computed(
    () =>
      user.value?.roles.name === 'admin' ||
      user.value?.roles.name === 'superadmin' ||
      user.value?.roles.name === 'RRHH' ||
      user.value?.isRRHH
  )

  const showProgressTray = computed(() => {
    return user.value?.roles.name === 'RRHH' || user.value?.isRRHH
  })

  const roleName = (role: string) => {
    return roleNames.get(role)
  }

  const items = ref([
    {
      label: 'Cambiar contraseña',
      icon: 'pi pi-lock',
      command: () => changePasswordDialogToggle(),
    },
    {
      label: 'Cerrar sesión',
      icon: 'pi pi-sign-out',
      command: () => logout(),
    },
    {
      separator: true,
    },
    {
      label: roleName(user.value?.roles.name || ''),
      icon: 'pi pi-user',
    },
  ])
</script>

<style scoped>
  .last-column {
    display: flex;
    flex-direction: row;
  }

  .p-menubar {
    display: flex;
    flex-direction: row;
    padding-inline: 2rem;
    position: fixed;
    background-color: white;
    z-index: 99;
    width: 100vw;
    border-radius: 0%;
    min-height: 70px;
    height: auto;
    align-items: center;
    justify-content: space-between;
  }

  .user-options {
    display: flex;
    position: relative;
    justify-content: space-around;
    flex-direction: row;
    border: 1px solid rgb(240, 240, 240);
    padding: 2px;
    background-color: #eef2ff;
    border-radius: 8px;
    box-sizing: border-box;
    cursor: pointer;
    box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.02), 0px 0px 2px rgba(0, 0, 0, 0.05),
      0px 1px 4px rgba(0, 0, 0, 0.08);
  }

  .options {
    display: flex;
    flex-direction: column;
    position: absolute;
    width: 100%;
    left: 0px;
    bottom: -162px;
    min-height: 10rem;
    height: auto;
    background: white;
    border: 1px solid rgb(240, 240, 240);
    z-index: 999;
    max-height: 74px;
    overflow-y: auto;
  }

  .opt:hover {
    width: 100%;
    background-color: aliceblue;
    font-weight: 600;
  }

  .user-options:hover {
    background-color: aliceblue;
  }

  :deep(.p-menubar-start) {
    display: flex;
    width: 60%;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  :deep(.p-dropdown) {
    margin-top: 0px !important;
  }

  :deep(.p-dropdown-label) {
    font-weight: 600;
    color: #2563eb !important;
    max-width: 100%;
  }

  :deep(.p-dropdown-clear-icon) {
    background-color: white !important;
    padding-left: 10px;
  }

  #logo-mid {
    display: none;
  }

  @media (max-width: 1200px) {
    #select-company {
      display: none !important;
    }
  }

  @media (max-width: 798px) {
    #company-logo {
      display: none;
    }
  }

  @media (max-width: 640px) {
    #logo {
      display: none;
    }

    #logo-mid {
      display: block;
      transform: scale(0.8, 0.8);
      content: url('@/assets/logo-blue.jpg');
    }

    #company-logo {
      display: none;
    }

    .user-options-details {
      display: none !important;
    }

    .options {
      width: 220px;
      left: -157px;
    }
  }

  @media (max-width: 320px) {
    .p-menubar {
      padding-inline: 1rem;
    }
  }

  @media (max-width: 285px) {
    #logo-mid {
      display: none;
    }
  }
</style>
